// UnscheduledReport.js – open-in-new-tab + reliable multi-page print via hidden iframe + inbox loading guard

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Tabs,
  TabList,
  Tab,
  useTheme,
  useColorMode,
  Link,
  Stack,
  SimpleGrid,
  useBreakpointValue,
  Button,
} from "@chakra-ui/react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faCheckCircle, faPrint } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, getUserForPatient } from "../../lib/Auth";

function UnscheduledReport() {
  // ---------------------------------------------------------------- State --
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const period = new URLSearchParams(window.location.hash.slice(1)).get("period");
    return /^[0-4]$/.test(period) ? Number(period) : 0;
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });

  // New: track temporary navigation/loading when resolving user id for inbox
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingRowId, setNavigatingRowId] = useState(null);

  // -------------------------------------------------------------- Theme ----
  const { colorMode } = useColorMode();
  const theme = useTheme();
  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  // ----------------------------------------------------------- Responsive --
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // ----------------------------------------------------------- Period Tabs --
  const periods = [
    { label: "0-6 Months", value: 0 },
    { label: "6-9 Months", value: 1 },
    { label: "9-12 Months", value: 2 },
    { label: "12-18 Months", value: 3 },
    { label: "18-36 Months", value: 4 },
  ];

  // --------------------------------------------- Helpers: transform row ----
  const transform = (item) => {
    const lastCall = item.calls?.length ? item.calls[item.calls.length - 1].created : null;
    const lastTextEmail = item.conversations?.length ? item.conversations[item.conversations.length - 1].created : null;

    // Hygiene due = lastCleaning + 6 months
    const lastCleaning = item.treatments?.[0]?.lastCleaning?.[0]?.completed_date || null;
    const hygieneDue = lastCleaning ? moment(lastCleaning).add(6, "months").toISOString() : moment(item.starttime).toISOString();

    // Determine if hygiene is past-due (before today)
    const hygienePastDue = hygieneDue ? moment(hygieneDue).isBefore(moment(), "day") : false;
    const hygieneProvider =
      item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0] &&
      `${item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0]?.first_name} ${item.treatments?.[0]?.lastCleaning?.[0]?.provider?.[0]?.last_name}`;

    // Outstanding (unscheduled) treatment total (cents)
    const outstanding = item.treatments?.[0]?.outstanding?.reduce((sum, o) => sum + (o.totalAmount || 0), 0) || 0;

    return {
      id: item._id,
      patient: `${item.patient?.first_name ?? ""} ${item.patient?.last_name ?? ""}`.trim(),
      lastVisit: item.starttime,
      provider: `${item.provider_first_name ?? ""} ${item.provider_last_name ?? ""}`.trim(),
      lastCleaning,
      hygieneProvider,
      hygieneDue,
      hygienePastDue,
      lastCall,
      lastTextEmail,
      outstanding,
      userId: item.user?._id,
      bid: item.bid,
      location_name: item.location_name,
      patient_guid: item.patient_guid,
    };
  };

  // Open inbox in a NEW TAB -------------------------------------------------
  const goToInbox = async (row) => {
    // Prevent concurrent attempts
    if (isNavigating) return;

    const needsLookup = !row.userId;
    const newSearchParams = new URLSearchParams(window.location.search);
    if (row.bid) {
      newSearchParams.set("bid", row.bid);
      newSearchParams.delete("bids");
    }

    // 1) Open the tab synchronously (user gesture) to avoid popup blockers
    //    We intentionally omit `noopener,noreferrer` so we can write a loading page
    //    and later redirect that same tab when the UID is ready.
    const child = window.open("", "_blank");

    // If popup blocker still blocked it, bail out to normal flow
    // (the overlay will still prevent further clicks and we will try a second open later)
    if (!child) {
      console.warn("Popup blocked even with immediate open. Falling back to late open.");
    } else {
      try {
        child.document.open();
        child.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Opening inbox…</title>
    <style>
      html,body{height:100%;margin:0;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,"Apple Color Emoji","Segoe UI Emoji"}
      .wrap{height:100%;display:flex;align-items:center;justify-content:center}
      .card{padding:16px 20px;border:1px solid #e5e7eb;border-radius:10px;box-shadow:0 10px 15px rgba(0,0,0,.05)}
      .spin{width:16px;height:16px;border-radius:9999px;border:2px solid #9ca3af;border-top-color:transparent;display:inline-block;animation:spin 1s linear infinite;margin-right:8px}
      @keyframes spin{to{transform:rotate(360deg)}}
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="card"><span class="spin"></span>Opening inbox…</div>
    </div>
  </body>
</html>`);
        child.document.close();
      } catch (e) {
        console.warn("Could not write loading page in child window", e);
      }
    }

    try {
      if (needsLookup) {
        setIsNavigating(true);
        setNavigatingRowId(row.id);
      }

      const uid = row.userId || (await getUserForPatient(row.bid, row.patient_guid));
      const finalUrl = `/inbox/${uid}?${newSearchParams.toString()}`;

      if (child) {
        // 2) Redirect the already-opened tab — no popup blocker
        try {
          child.location.replace(finalUrl);
        } catch (e) {
          // Fallback if replace fails (rare cross-origin edge cases)
          child.location.href = finalUrl;
        }
      } else {
        // 3) Last resort: try opening now (may be blocked)
        window.open(finalUrl, "_blank");
      }
    } catch (err) {
      console.error("Error opening inbox:", err);
      if (child) {
        try {
          child.document.open();
          child.document.write(`<p style="font-family:ui-sans-serif,system-ui; padding:16px;">Failed to open inbox. Please close this tab and try again.</p>`);
          child.document.close();
        } catch (writeError) {
          console.error("Error writing fallback message:", writeError);
        }
      }
    } finally {
      if (needsLookup) {
        setIsNavigating(false);
        setNavigatingRowId(null);
      }
    }
  };

  // Reliable multi-page print via hidden iframe -----------------------------
  const handlePrint = () => {
    const source = document.getElementById("unscheduled-print");
    if (!source) return;

    // Create hidden iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;

    // Copy styles (Chakra/Emotion and external CSS)
    const styleEls = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join("\n");

    // Inherit classes/attrs that affect theming (Chakra color mode, etc.)
    const htmlClass = document.documentElement.className || "";
    const bodyClass = document.body.className || "";
    const bodyDataTheme = document.body.getAttribute("data-theme") || "";
    const bodyStyleAttr = document.body.getAttribute("style") || "";

    // Build HTML shell
    doc.open();
    doc.write(`<!doctype html>
  <html class="${htmlClass}">
  <head>
    <meta charset="utf-8"/>
    <title>Unscheduled Report</title>
    <base href="${document.baseURI}">
    ${styleEls}
    <style>
      @page { size: auto; margin: 12mm; }
      html, body { height: auto !important; overflow: visible !important; }

      /* Ensure the printed content isn't constrained */
      #unscheduled-print {
        max-height: none !important;
        overflow: visible !important;
        padding: 0 !important;
        width: 100% !important;
        min-width: 0 !important; /* override your minW=1100px for paper */
      }

      /* If you want to avoid splitting a row across pages, add this class in your JSX */
      .print-avoid-break { break-inside: avoid; page-break-inside: avoid; }

      /* Better color fidelity for backgrounds/icons */
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

      /* Hide UI controls if any were copied */
      .no-print { display: none !important; }
    </style>
  </head>
  <body class="${bodyClass}" ${bodyDataTheme ? `data-theme="${bodyDataTheme}"` : ""} style="${bodyStyleAttr}">
    <div id="print-root"></div>
  </body>
  </html>`);
    doc.close();

    // Clone the panel DOM into the iframe
    const clone = source.cloneNode(true);
    clone.style.maxHeight = "none";
    clone.style.overflow = "visible";
    clone.style.minWidth = "0"; // override minW=1100px on paper

    const mount = doc.getElementById("print-root");
    mount.appendChild(clone);

    // Wait for styles to resolve, then print & cleanup
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 400); // bump if fonts load slowly (e.g., 800ms)
  };
  

  // ------------------------------------------------------ Fetch & setRows --
  const fetchData = async (period = 0) => {
    setIsLoading(true);
    try {
      const resp = await cqAPI.get(`/reports/unscheduled?period=${period}`);
      if (resp.type === "ok") {
        setRows(resp.data.map(transform));
      }
    } catch (err) {
      console.error("Error fetching unscheduled patients:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.location.hash = new URLSearchParams({ period: selectedPeriod }).toString();
    fetchData(selectedPeriod);
  }, [selectedPeriod]);

  // ----------------------------------------------------------- Sorting ----
  const sortedRows = useMemo(() => {
    const items = [...rows];
    if (sortConfig.key !== null) {
      items.sort((a, b) => {
        const { key } = sortConfig;
        if (["patient", "provider", "hygieneProvider"].includes(key)) {
          return sortConfig.direction === "ascending" ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
        }
        if (key === "outstanding") {
          return sortConfig.direction === "ascending" ? a.outstanding - b.outstanding : b.outstanding - a.outstanding;
        }
        if (key === "hygienePastDue") {
          const va = a.hygienePastDue ? 1 : 0;
          const vb = b.hygienePastDue ? 1 : 0;
          return sortConfig.direction === "ascending" ? va - vb : vb - va;
        }
        const va = a[key] ? new Date(a[key]) : new Date(0);
        const vb = b[key] ? new Date(b[key]) : new Date(0);
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [rows, sortConfig]);
  const showLocationColumn = sortedRows.some((row) => row.location_name);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  // ----------------------------------------------------------- Column map --
  const columns = [
    { key: "patient", label: "Patient", basis: { base: "160px", lg: "14%" }, click: true },
    { key: "lastVisit", label: "Last Visit", basis: { base: "120px", lg: "10%" } },
    { key: "provider", label: "Provider", basis: { base: "120px", lg: "10%" } },
    { key: "lastCleaning", label: "Last Hygiene", basis: { base: "120px", lg: "10%" } },
    { key: "hygieneProvider", label: "Hygiene Provider", basis: { base: "160px", lg: "12%" } },
    { key: "hygieneDue", label: "Hygiene Due", basis: { base: "120px", lg: "10%" } },
    { key: "hygienePastDue", label: "Past Due", basis: { base: "70px", lg: "6%" }, center: true },
    { key: "lastCall", label: "Last Call", basis: { base: "120px", lg: "10%" }, hideBelow: "lg" },
    { key: "lastTextEmail", label: "Last Text/Email", basis: { base: "150px", lg: "11%" }, hideBelow: "lg" },
    { key: "outstanding", label: "Unscheduled $", basis: { base: "120px", lg: "10%" }, alignRight: true },
    ...(showLocationColumn ? [{ key: "location_name", label: "Location", basis: { base: "150px", lg: "12%" } }] : []),
  ];

  // ----------------------------------------------------------- Loading ----
  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  // -------------------------------------------------------------- Render --
  return (
    <Box id="unscheduled-print" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Controls */}
      <Flex className="no-print" justify="flex-end" mb={2}>
        <Button
          leftIcon={<FontAwesomeIcon icon={faPrint} />}
          variant="outline"
          size={isMobile ? "sm" : "md"}
          onClick={handlePrint}
          isDisabled={isNavigating}
        >
          Print
        </Button>
      </Flex>

      {/* Period selector (not printed) */}
      <Tabs
        className="no-print"
        variant="enclosed"
        colorScheme="blue"
        index={selectedPeriod}
        onChange={(i) => !isNavigating && setSelectedPeriod(i)}
        mb={4}
        isFitted={isMobile}
        size={isMobile ? "sm" : "md"}
        isLazy
      >
        <TabList>
          {periods.map((p) => (
            <Tab key={p.value} isDisabled={isNavigating}>{p.label}</Tab>
          ))}
        </TabList>
      </Tabs>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      {/* Mobile layout */}
      {isMobile ? (
        <Stack spacing={3} className="print-tight" aria-busy={isNavigating}>
          {sortedRows.map((r) => (
            <Box
              key={r.id}
              p={3}
              borderWidth="1px"
              borderRadius="lg"
              _hover={{ bg: hoverBgColor }}
              fontSize="sm"
              className="print-avoid-break"
              opacity={isNavigating && !r.userId ? 0.7 : 1}
            >
              <HStack justifyContent="space-between" alignItems="flex-start">
                <HStack>
                  <Link
                    onClick={() => goToInbox(r)}
                    fontWeight="bold"
                    textDecoration="underline"
                    aria-disabled={isNavigating}
                    tabIndex={isNavigating ? -1 : 0}
                    _hover={{ cursor: isNavigating ? "not-allowed" : "pointer" }}
                  >
                    {r.patient || "—"} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: "4px" }} />
                  </Link>
                  {isNavigating && navigatingRowId === r.id && !r.userId ? (
                    <Spinner size="sm" />
                  ) : null}
                </HStack>
                {r.outstanding ? <Text fontWeight="bold">${(r.outstanding).toFixed(2)}</Text> : null}
              </HStack>
              <SimpleGrid columns={2} spacingY={1} mt={2}>
                {showLocationColumn && (
                  <>
                    <Text color="gray.500">Location</Text>
                    <Text>{r.location_name || "—"}</Text>
                  </>
                )}
                <Text color="gray.500">Last Visit</Text>
                <Text>{r.lastVisit ? moment(r.lastVisit).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Provider</Text>
                <Text>{r.provider || "—"}</Text>
                <Text color="gray.500">Last Hygiene</Text>
                <Text>{r.lastCleaning ? moment(r.lastCleaning).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Hyg. Provider</Text>
                <Text>{r.hygieneProvider || "—"}</Text>
                <Text color="gray.500">Hygiene Due</Text>
                <Text>{r.hygieneDue ? moment(r.hygieneDue).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Past Due</Text>
                <Box>{r.hygienePastDue && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.red[500] }} />}</Box>
                <Text color="gray.500">Last Call</Text>
                <Text>{r.lastCall ? moment(r.lastCall).format("MMM D YYYY") : "—"}</Text>
                <Text color="gray.500">Last Text/Email</Text>
                <Text>{r.lastTextEmail ? moment(r.lastTextEmail).format("MMM D YYYY") : "—"}</Text>
              </SimpleGrid>
            </Box>
          ))}
        </Stack>
      ) : (
        /* Desktop/tablet layout */
        <VStack spacing={0} align="stretch" minW="1100px" className="print-tight" aria-busy={isNavigating}>
          {/* Header */}
          <Flex borderBottom="1px" borderColor="gray.200" p={2} bg={hoverBgColor} fontWeight="bold" fontSize="sm" className="print-avoid-break">
            <HStack spacing={4} flex="1">
              {columns.map((c) => (
                <Text
                  key={c.key}
                  flexBasis={c.basis}
                  cursor="pointer"
                  textAlign={c.center ? "center" : c.alignRight ? "right" : "left"}
                  display={c.hideBelow ? { base: "none", [c.hideBelow]: "block" } : "block"}
                  onClick={() => !isNavigating && requestSort(c.key)}
                  opacity={isNavigating ? 0.6 : 1}
                >
                  {c.label}
                </Text>
              ))}
            </HStack>
          </Flex>

          {/* Rows */}
          {sortedRows.map((r) => (
            <Flex key={r.id} p={2} _hover={{ bg: hoverBgColor }} fontSize="sm" className="print-avoid-break" opacity={isNavigating && !r.userId ? 0.7 : 1}>
              <HStack spacing={4} flex="1" alignItems="center">
                <HStack flexBasis={columns[0].basis} spacing={2}>
                  <Link
                    onClick={() => goToInbox(r)}
                    textDecoration="underline"
                    aria-disabled={isNavigating}
                    tabIndex={isNavigating ? -1 : 0}
                    _hover={{ cursor: isNavigating ? "not-allowed" : "pointer" }}
                  >
                    {r.patient || "—"} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: "4px" }} />
                  </Link>
                  {isNavigating && navigatingRowId === r.id && !r.userId ? (
                    <Spinner size="sm" />
                  ) : null}
                </HStack>

                <Text flexBasis={columns[1].basis}>{r.lastVisit ? moment(r.lastVisit).format("MMM D YYYY") : "—"}</Text>
                <Text flexBasis={columns[2].basis}>{r.provider || "—"}</Text>
                <Text flexBasis={columns[3].basis}>{r.lastCleaning ? moment(r.lastCleaning).format("MMM D YYYY") : "—"}</Text>
                <Text flexBasis={columns[4].basis}>{r.hygieneProvider || "—"}</Text>
                <Text flexBasis={columns[5].basis}>{r.hygieneDue ? moment(r.hygieneDue).format("MMM D YYYY") : "—"}</Text>
                <Box flexBasis={columns[6].basis} textAlign="center">
                  {r.hygienePastDue && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.red[500] }} />}
                </Box>
                <Text display={{ base: "none", lg: "block" }} flexBasis={columns[7].basis}>
                  {r.lastCall ? moment(r.lastCall).format("MMM D YYYY") : "—"}
                </Text>
                <Text display={{ base: "none", lg: "block" }} flexBasis={columns[8].basis}>
                  {r.lastTextEmail ? moment(r.lastTextEmail).format("MMM D YYYY") : "—"}
                </Text>
                <Text flexBasis={columns[9].basis} textAlign="right">
                  {r.outstanding ? `$${(r.outstanding).toFixed(2)}` : "—"}
                </Text>
                {showLocationColumn && (
                  <Text flexBasis={columns[10].basis}>
                    {r.location_name || "—"}
                  </Text>
                )}
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}

      {/* Total */}
      <Box textAlign="right" mt={2} className="print-avoid-break">
        <Text fontWeight="bold">Total Patients: {rows.length}</Text>
      </Box>

      {/* Global overlay to block interaction while resolving getUserForPatient */}
      {isNavigating && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          align="center"
          justify="center"
          bg="blackAlpha.300"
          zIndex={1000}
          className="no-print"
        >
          <HStack spacing={3} p={4} bg={colorMode === "light" ? "white" : "gray.700"} borderRadius="md" boxShadow="lg">
            <Spinner size="md" />
            <Text>Opening inbox…</Text>
          </HStack>
        </Flex>
      )}
    </Box>
  );
}

export default UnscheduledReport;
